import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase';
import { collection, getDocs, query, doc, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Container,
  Typography,
  Box,
  TextField,
  Snackbar,
  Alert,
  Modal,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import { differenceInDays, addDays, isAfter } from 'date-fns';

// Modern color palette
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled('button')(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  backgroundColor: colors.primary,
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colors.accent,
  },
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 400 },
  maxWidth: '400px',
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(12px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  padding: theme.spacing(4),
  textAlign: 'center',
  animation: 'fadeInScale 0.3s ease-out',
  '@keyframes fadeInScale': {
    '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.8)' },
    '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [trialDaysToAdd, setTrialDaysToAdd] = useState('');
  const [resendingUserId, setResendingUserId] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const usersCollection = collection(db, 'users');
        const usersQuery = query(usersCollection);
        const querySnapshot = await getDocs(usersQuery);
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbarMessage('Error fetching users');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = users
    .filter((user) =>
      (user.fullName?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(searchText.toLowerCase())
    )
    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleOpenTrialModal = (userId) => {
    setSelectedUserId(userId);
    setTrialDaysToAdd('');
    setOpenTrialModal(true);
  };

  const handleCloseTrialModal = () => {
    setOpenTrialModal(false);
    setSelectedUserId(null);
    setTrialDaysToAdd('');
  };

  const handleAddTrialDays = async () => {
    const days = parseInt(trialDaysToAdd, 10);
    if (!days || days <= 0) {
      setSnackbarMessage('Please enter a valid number of days (greater than 0).');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const userDocRef = doc(db, `users/${selectedUserId}`);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        setSnackbarMessage('User not found.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const userData = userDoc.data();
      const currentTrialStartDate = userData.trialStartDate?.toDate();
      const currentDate = new Date();
      let newTrialStartDate;

      if (currentTrialStartDate) {
        // Calculate remaining days and adjust trialStartDate to maintain a 7-day trial period
        const trialEndDate = addDays(currentTrialStartDate, 7);
        const remainingDays = Math.max(0, differenceInDays(trialEndDate, currentDate));
        const totalTrialDays = remainingDays + days;
        newTrialStartDate = addDays(currentDate, - (7 - totalTrialDays));
      } else {
        // If no trialStartDate, treat as a new trial
        newTrialStartDate = addDays(currentDate, - (7 - days));
      }

      await updateDoc(userDocRef, { trialStartDate: newTrialStartDate });
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUserId ? { ...user, trialStartDate: newTrialStartDate } : user
        )
      );
      setSnackbarMessage(`Successfully added ${days} trial day${days > 1 ? 's' : ''} for user.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseTrialModal();
    } catch (error) {
      console.error('Error adding trial days:', error);
      setSnackbarMessage('Failed to add trial days.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleResendVerification = async (email, userId) => {
    setResendingUserId(userId);
    try {
      const resendVerificationEmail = httpsCallable(functions, 'resendVerificationEmail');
      const result = await resendVerificationEmail({ email });
      setSnackbarMessage(result.data?.message || 'Verification email sent.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error resending verification email:', error);
      setSnackbarMessage(error.message || 'Failed to send verification email.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResendingUserId(null);
    }
  };

  const getTrialDaysRemaining = (user) => {
    if (!user || user.status !== 'trial' || !user.trialStartDate) return 'N/A';
    const trialEndDate = addDays(user.trialStartDate.toDate(), 7);
    const currentDate = new Date();
    return isAfter(currentDate, trialEndDate) ? 'Expired' : differenceInDays(trialEndDate, currentDate);
  };

  const columns = [
    { field: 'fullName', headerName: 'Name', width: 200 },
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'role', headerName: 'Role', width: 150 },
    { field: 'status', headerName: 'Status', width: 150 },
    {
      field: 'trialDaysRemaining',
      headerName: 'Trial Days Remaining',
      width: 150,
      valueGetter: (params) => getTrialDaysRemaining(params?.row),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 380,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Link to={`/admin/users/edit/${params.row?.id || ''}`}>
            <AnimatedButton disabled={!params.row}>Edit</AnimatedButton>
          </Link>
          {params.row?.status === 'trial' && (
            <AnimatedButton
              onClick={() => handleOpenTrialModal(params.row.id)}
              disabled={!params.row}
            >
              Add Trial Days
            </AnimatedButton>
          )}
          {params.row && !params.row.emailVerified && (
            <AnimatedButton
              onClick={() => handleResendVerification(params.row.email, params.row.id)}
              disabled={resendingUserId === params.row.id}
            >
              {resendingUserId === params.row.id ? 'Sending…' : 'Resend Verification'}
            </AnimatedButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ fontSize: 35, mr: 1 }} />
            Manage Users
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            View and edit user details in the portal.
          </Typography>
        </Box>

        <ModernCard>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: colors.secondary, fontWeight: '600' }}>
                User List
              </Typography>
              <TextField
                label="Search Users"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search by name or email"
                sx={{
                  width: { xs: '100%', sm: '250px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '& fieldset': { borderColor: colors.primary },
                    '&:hover fieldset': { borderColor: colors.accent },
                    '&.Mui-focused fieldset': { borderColor: colors.accent },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.secondary,
                    '&.Mui-focused': { color: colors.accent },
                  },
                }}
              />
            </Box>
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              pageSize={10}
              checkboxSelection={false}
              loading={isLoading}
              initialState={{
                sorting: { sortModel: [{ field: 'fullName', sort: 'asc' }] },
              }}
              sx={{
                '& .MuiDataGrid-root': { border: 'none' },
                '& .MuiDataGrid-cell': { color: colors.secondary },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.background,
                  color: colors.secondary,
                  fontWeight: '600',
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
              }}
            />
          </Box>
        </ModernCard>

        <Modal
          open={openTrialModal}
          onClose={handleCloseTrialModal}
          sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' } }}
        >
          <ModalContent>
            <IconButton
              onClick={handleCloseTrialModal}
              sx={{ position: 'absolute', top: 12, right: 12, color: colors.secondary }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: '700', color: colors.secondary, mb: 2 }}>
              Add Trial Days
            </Typography>
            <Typography variant="body1" sx={{ color: colors.secondary, mb: 3, opacity: 0.85, fontSize: '1.1rem' }}>
              Enter the number of days to add to the user's trial period.
            </Typography>
            <TextField
              label="Additional Trial Days"
              type="number"
              value={trialDaysToAdd}
              onChange={(e) => setTrialDaysToAdd(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="E.g., 5"
              inputProps={{ min: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <AnimatedButton
                onClick={handleCloseTrialModal}
                sx={{ backgroundColor: '#e0e0e0', color: colors.secondary, '&:hover': { backgroundColor: '#d0d0d0' } }}
              >
                Cancel
              </AnimatedButton>
              <AnimatedButton onClick={handleAddTrialDays}>
                Add Days
              </AnimatedButton>
            </Box>
          </ModalContent>
        </Modal>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? colors.primary : '#e74c3c', color: 'white' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AdminUsers;